/**
 * Consultas del Portal Condominio que arma el servidor (migraciones 20261015210100 y siguientes).
 * Reemplazan, menú por menú, a `fetchCondominioDataset`, que armaba todo en el navegador con ~80
 * peticiones en serie y sin filtrar acuerdos inactivos. El detalle de una cuenta es el de Cobranza
 * (`CobranzaCuentaDetalle`), así que no tiene consulta propia aquí.
 */
import { supabase } from "@/integrations/supabase/client";
import { esRpcInexistente } from "@/lib/rpcErrors";
import { normalizarUnidad, type TendenciaCondominio, type UnidadCondominioRpc } from "./unidades";
import type { CondominioRef } from "@/types/condominio";
import { normalizarCargo, normalizarPago, type CargoCobranza, type PagoCobranza } from "./cobranza";
import { normalizarMesTesoreria, type MesTesoreria } from "./tesoreria";
import { normalizarEvento, type EventoBitacora } from "./auditoria";

/** La RPC aún no está en este ambiente: la pantalla lo dice en vez de mostrar ceros. */
export class RpcCondominioPendiente extends Error {
  constructor() {
    super("Se activa cuando se aplique la migración del Portal Condominio.");
  }
}

const sb = supabase as any;

/**
 * "Ver como" del Portal Condominio: con `verComo` (auth_user_id del impersonado) el servidor
 * calcula permiso y alcance con SU identidad (20261022095845; solo si el usuario real puede
 * impersonar). Sin él, con la sesión. Solo lecturas: las escrituras nunca lo mandan.
 */
const conVerComo = <T extends Record<string, unknown>>(args: T, verComo?: string | null) =>
  verComo ? { ...args, p_auth_user_id: verComo } : args;

/** `null` si la RPC aún no existe (el llamador usa su consulta anterior). */
export async function fetchCondominiosRpc(verComo?: string | null): Promise<CondominioRef[] | null> {
  const { data, error } = await sb.rpc("get_condominios", conVerComo({}, verComo));
  if (error) {
    if (esRpcInexistente(error)) return null;
    throw error;
  }
  return ((data as { id: number; nombre: string }[]) ?? []).map((p) => ({ id: Number(p.id), nombre: p.nombre }));
}

export async function fetchUnidadesCondominio(proyectoId: number, verComo?: string | null): Promise<UnidadCondominioRpc[]> {
  const { data, error } = await sb.rpc("get_condominio_unidades", conVerComo({ p_id_proyecto: proyectoId }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map(normalizarUnidad);
}

export async function fetchTendenciaCondominio(proyectoId: number, meses = 12, verComo?: string | null): Promise<TendenciaCondominio[]> {
  const { data, error } = await sb.rpc("get_condominio_tendencia", conVerComo({ p_id_proyecto: proyectoId, p_meses: meses }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map((f) => ({
    mes: String(f.mes).slice(0, 10),
    mtto_esperado: Number(f.mtto_esperado) || 0,
    fondo_esperado: Number(f.fondo_esperado) || 0,
    cobrado: Number(f.cobrado) || 0,
  }));
}

/** Pagos de las cuentas de mantenimiento en el periodo, con su conciliación (20261015230000). */
export async function fetchPagosCondominio(proyectoId: number, desde: string | null, hasta: string | null, verComo?: string | null): Promise<PagoCobranza[]> {
  const { data, error } = await sb.rpc("get_condominio_pagos", conVerComo({ p_id_proyecto: proyectoId, p_desde: desde, p_hasta: hasta }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map(normalizarPago);
}

/** Cuotas de mantenimiento, fondo de reserva y multas del periodo con su estatus (20261015230000). */
export async function fetchCargosCondominio(proyectoId: number, desde: string | null, hasta: string | null, verComo?: string | null): Promise<CargoCobranza[]> {
  const { data, error } = await sb.rpc("get_condominio_cargos", conVerComo({ p_id_proyecto: proyectoId, p_desde: desde, p_hasta: hasta }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map(normalizarCargo);
}

/** Ingresos por mes de las cuentas de mantenimiento y su aplicación (20261016000000). */
export async function fetchTesoreriaCondominio(proyectoId: number, meses: number, verComo?: string | null): Promise<MesTesoreria[]> {
  const { data, error } = await sb.rpc("get_condominio_tesoreria", conVerComo({ p_id_proyecto: proyectoId, p_meses: meses }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map(normalizarMesTesoreria);
}

/** Bitácora de eventos reales del condominio en el rango (20261016010000). */
export async function fetchBitacoraCondominio(proyectoId: number, desde: string | null, hasta: string | null, verComo?: string | null): Promise<EventoBitacora[]> {
  const { data, error } = await sb.rpc("get_condominio_bitacora", conVerComo({ p_id_proyecto: proyectoId, p_desde: desde, p_hasta: hasta }, verComo));
  if (error) {
    if (esRpcInexistente(error)) throw new RpcCondominioPendiente();
    throw error;
  }
  return ((data as Record<string, unknown>[]) ?? []).map(normalizarEvento);
}
